import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow, parseISO } from "date-fns";
import { BellRing, CheckCheck, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { pushStatus, enablePush, disablePush } from "@/lib/push";
import { PageHeader, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const FILTERS = { all: () => true, rosters: (t) => t.startsWith("roster"), tasks: (t) => t.startsWith("task"), holidays: (t) => t.startsWith("holiday") };

const PushCard = () => {
  const [status, setStatus] = useState("checking");
  useEffect(() => {
    pushStatus().then(setStatus).catch(() => setStatus("unsupported"));
  }, []);
  const toggle = async () => {
    try {
      if (status === "enabled") {
        await disablePush();
        setStatus("disabled");
        toast.success("Push notifications turned off on this device");
      } else {
        await enablePush();
        setStatus("enabled");
        toast.success("Push notifications enabled on this device");
      }
    } catch (e) {
      toast.error(e?.response ? errMsg(e) : e.message);
      pushStatus().then(setStatus);
    }
  };
  const test = async () => {
    try {
      const { data } = await api.post("/push/test");
      toast.success(data.sent ? "Test push sent" : "No devices subscribed yet");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const label = { checking: "Checking…", enabled: "On for this device", disabled: "Off", denied: "Blocked in browser settings", unsupported: "Not supported on this browser" }[status];
  return (
    <div className="rounded-3xl bg-[#24474A] text-white p-5 sm:p-6 mb-6 flex flex-col md:flex-row md:items-center gap-4 fade-up" data-testid="push-card">
      <span className="w-12 h-12 rounded-2xl bg-white/10 grid place-items-center"><Smartphone className="w-6 h-6" /></span>
      <div className="flex-1">
        <div className="font-bold text-lg">Mobile & desktop push notifications</div>
        <div className="text-sm text-white/75">Get alerts for schedule changes, new tasks and leave decisions even when the app is closed. On iPhone, add the app to your Home Screen first. Status: <span className="font-bold text-white" data-testid="push-status">{label}</span></div>
      </div>
      <div className="flex gap-2">
        {status === "enabled" && <Button variant="secondary" className="rounded-full" onClick={test} data-testid="push-test-btn">Send test</Button>}
        <Button variant="secondary" className="rounded-full" disabled={["checking", "denied", "unsupported"].includes(status)} onClick={toggle} data-testid="push-toggle-btn">
          <BellRing className="w-4 h-4 mr-1.5" /> {status === "enabled" ? "Turn off" : "Enable push"}
        </Button>
      </div>
    </div>
  );
};

export default function Notifications() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const { data } = await api.get("/notifications");
    setData(data);
    window.dispatchEvent(new Event("onn:refresh-notifications"));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const readAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };
  const open = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`);
    navigate(n.link || "/");
  };

  const items = data.items.filter((n) => FILTERS[filter](n.type));
  return (
    <div>
      <PageHeader title="Notifications" subtitle={`${data.unread} unread`}>
        <Button variant="outline" className="rounded-full" onClick={readAll} disabled={!data.unread} data-testid="mark-all-read-btn"><CheckCheck className="w-4 h-4 mr-1.5" /> Mark all read</Button>
      </PageHeader>
      <PushCard />
      <Tabs value={filter} onValueChange={setFilter} className="mb-4">
        <TabsList className="rounded-full">
          {Object.keys(FILTERS).map((k) => <TabsTrigger key={k} value={k} className="rounded-full capitalize" data-testid={`notif-filter-${k}`}>{k}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="notifications-list">
        {items.map((n) => (
          <button key={n.id} onClick={() => open(n)} className={`w-full text-left flex gap-3 px-4 sm:px-5 py-4 hover:bg-slate-50 transition-colors ${n.read ? "" : "bg-[#EAF2F2]/40"}`} data-testid={`notification-item-${n.id}`}>
            <span className={`mt-2 w-2 h-2 rounded-full shrink-0 ${n.read ? "bg-slate-200" : "bg-[#24474A]"}`} />
            <span className="flex-1 min-w-0">
              <span className="flex justify-between gap-3">
                <span className="font-bold text-slate-800">{n.title}</span>
                <span className="text-xs text-slate-400 whitespace-nowrap">{formatDistanceToNow(parseISO(n.created_at), { addSuffix: true })}</span>
              </span>
              <span className="block text-sm text-slate-600">{n.body}</span>
              {n.meta?.changes?.length > 0 && (
                <ul className="mt-2 text-sm text-orange-900 bg-orange-50 border border-orange-200 rounded-xl px-4 py-2 list-disc pl-8" data-testid={`notification-changes-${n.id}`}>
                  {n.meta.changes.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              )}
            </span>
          </button>
        ))}
        {items.length === 0 && <Empty>No notifications.</Empty>}
      </div>
    </div>
  );
}
